/*
  NO FUGAZI — cookie consent.

  Three jobs, in order of who cares:

  1. The visitor: nothing non-essential runs until they say so, rejecting is one
     click, and the choice can be changed later from the footer.
  2. Google Ads and Meta: Consent Mode v2 signals are updated the moment a choice
     is made. Denied traffic still gets modelled conversions, which is why a
     compliant banner costs far less measurement than people assume — and why a
     banner that harvests invalid consent is the worse commercial option, since
     invalid consent means no lawful basis for any of the data behind it.
  3. Shopify: its own _shopify_y / _shopify_s analytics cookies are gated through
     the Customer Privacy API. Measured before this shipped, both were being set
     on a cold first visit with no consent at all.

  Region gating is delegated to Shopify's shouldShowBanner(), which knows where
  the visitor actually is. Outside the UK/EEA there is no banner and no denied
  window — that is the single biggest lever on measurement loss, and it is
  legitimate, unlike pre-ticking boxes.

  Notably NOT done: no pre-ticked non-essential boxes, and no "confirm choices"
  as the only route out. Both produce consent that is invalid under UK GDPR, so
  the tracking they enable has no lawful basis to stand on.
*/
(function () {
  'use strict';

  var KEY = 'nf_consent_v1';
  var VERSION = 1;
  var root = null;
  var els = {};

  function gtag() { window.dataLayer = window.dataLayer || []; window.dataLayer.push(arguments); }

  function read() {
    try {
      var v = JSON.parse(window.localStorage.getItem(KEY) || 'null');
      return v && v.version === VERSION ? v : null;
    } catch (e) { return null; }
  }

  function write(analytics, marketing) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({
        version: VERSION,
        analytics: !!analytics,
        marketing: !!marketing,
        at: new Date().toISOString()
      }));
    } catch (e) { /* private mode: the choice holds for this page view only */ }
  }

  function g(v) { return v ? 'granted' : 'denied'; }

  function apply(analytics, marketing, announce) {
    gtag('consent', 'update', {
      ad_storage:              g(marketing),
      ad_user_data:            g(marketing),
      ad_personalization:      g(marketing),
      analytics_storage:       g(analytics),
      functionality_storage:   g(analytics),
      personalization_storage: g(marketing),
      security_storage:        'granted'
    });
    gtag('set', 'ads_data_redaction', !marketing);

    // Gate Shopify's own analytics cookies through its Customer Privacy API.
    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          if (err) return;
          try {
            window.Shopify.customerPrivacy.setTrackingConsent({
              analytics: !!analytics,
              marketing: !!marketing,
              preferences: !!analytics,
              sale_of_data: !!marketing
            }, function () {});
          } catch (e) { /* API shape changed; the Consent Mode signals still stand */ }
        });
      }
    } catch (e) { /* never let consent plumbing break the page */ }

    if (announce) {
      try {
        window.dataLayer.push({
          event: 'consent_update',
          consent_analytics: !!analytics,
          consent_marketing: !!marketing
        });
      } catch (e) {}
    }
  }

  function hide() {
    if (!root) return;
    root.classList.remove('is--in');
    window.setTimeout(function () { if (root) root.hidden = true; }, 280);
  }

  function show() {
    if (!root) return;
    root.hidden = false;
    // force a frame so the transform transition actually runs
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { root.classList.add('is--in'); });
    });
  }

  function decide(analytics, marketing) {
    write(analytics, marketing);
    apply(analytics, marketing, true);
    hide();
  }

  /* The mobile toolbar is fixed to the bottom too. Measure it rather than
     hard-coding a height, so the two never sit on top of each other. */
  function offsetForToolbar() {
    if (!root) return;
    /* '.t4s-toolbar-mobile' matches the SECTION WRAPPER, which is static: the
       element actually fixed to the bottom edge is '.t4s-toolbar' inside it.
       Measuring the wrapper left the offset at 0, so the consent sheet painted
       straight over the bar's WhatsApp button on every first mobile visit. */
    var bars = document.querySelectorAll('.t4s-toolbar, .t4s-toolbar-mobile, [class*="toolbar_mobile"]');
    var h = 0;
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      var rect = el.getBoundingClientRect();
      if (!rect.height) continue;
      if (cs.position === 'fixed' || Math.abs(rect.bottom - window.innerHeight) < 2) {
        h = Math.max(h, Math.round(rect.height));
      }
    }
    root.style.setProperty('--nf-cc-offset', h + 'px');
  }

  function wire() {
    root = document.getElementById('nf-cc');
    if (!root) return false;

    els.panel   = root.querySelector('[data-nf-cc-panel]');
    els.analytics = root.querySelector('#nf-cc-analytics');
    els.marketing = root.querySelector('#nf-cc-marketing');

    root.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-nf-cc]') : null;
      if (!t) return;
      var act = t.getAttribute('data-nf-cc');

      if (act === 'accept') { decide(true, true); }
      else if (act === 'reject') { decide(false, false); }
      else if (act === 'manage') {
        var open = els.panel.hasAttribute('hidden');
        if (open) { els.panel.removeAttribute('hidden'); } else { els.panel.setAttribute('hidden', ''); }
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      else if (act === 'save') {
        decide(els.analytics && els.analytics.checked, els.marketing && els.marketing.checked);
      }
    });

    // Footer / anywhere: let the visitor reopen and withdraw consent.
    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-nf-consent-open]') : null;
      if (!t) return;
      e.preventDefault();
      var cur = read();
      if (els.analytics) els.analytics.checked = !!(cur && cur.analytics);
      if (els.marketing) els.marketing.checked = !!(cur && cur.marketing);
      if (els.panel) els.panel.removeAttribute('hidden');
      offsetForToolbar();
      show();
    });

    window.addEventListener('resize', offsetForToolbar, { passive: true });
    return true;
  }

  function start() {
    if (!wire()) return;

    var stored = read();
    if (stored) {
      // Already chosen. The init snippet has already set these as defaults, but
      // re-apply so the Shopify API is told on every page view.
      apply(stored.analytics, stored.marketing, false);
      return;
    }

    // Global Privacy Control is a legally recognised opt-out signal in several
    // jurisdictions. Honour it without asking, and do not nag.
    if (navigator.globalPrivacyControl === true) {
      decide(false, false);
      return;
    }

    var shown = false;
    function reveal() {
      if (shown) return;
      shown = true;
      offsetForToolbar();
      show();
    }

    /* Shopify's shouldShowBanner() only returns true once the shop's Customer
       Privacy regions are configured in the admin. Measured on this store it
       returns FALSE for a UK visitor, because that setting has never been set —
       so relying on it alone means no UK visitor is ever asked, which is the
       exact gap this whole file exists to close.

       So it is treated as one vote, not the decision. A Europe/* timezone is
       taken as "ask anyway". That deliberately over-covers — it catches
       non-EEA European countries too — because showing a banner to someone who
       did not legally need one costs a click, while missing someone who did is
       the breach. */
    function inEuropeanTimezone() {
      try {
        var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        return tz.indexOf('Europe/') === 0 || tz === 'GB' || tz === 'Atlantic/Canary' || tz === 'Atlantic/Madeira';
      } catch (e) {
        return true; // cannot tell: ask.
      }
    }

    var settled = false;
    function settle(needed) {
      if (settled) return;
      settled = true;
      if (needed || inEuropeanTimezone()) {
        reveal();
      } else {
        // Genuinely outside a consent region: grant, and never show a banner.
        write(true, true);
        apply(true, true, false);
      }
    }

    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          if (err) return settle(true);
          try {
            settle(window.Shopify.customerPrivacy.shouldShowBanner());
          } catch (e) { settle(true); }
        });
        // If the feature never calls back, default to asking.
        window.setTimeout(function () { settle(true); }, 2500);
      } else {
        settle(true);
      }
    } catch (e) { settle(true); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
